import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LeafIcon, XIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
export function WineCollectionSection() {
  const navigate = useNavigate();
  const [selectedWine, setSelectedWine] = useState<number | null>(null);
  const wines = [{
    name: 'Saumur Champigny',
    variety: 'Cabernet Franc',
    origin: 'Loire Valley, France',
    price: '$21',
    description: 'Our signature red. Elegant, fruit-forward Cabernet Franc with silky tannins and a vibrant finish. Crafted at Chateau de Chaintres.',
    image: 'https://cdn.magicpatterns.com/uploads/jjQYV3XY2yBUSWDCGJJ5m5/Screenshot_2026-03-23_at_8.45.35_PM.png',
    badge: 'Signature Red'
  }, {
    name: 'Saumur Blanc',
    variety: 'Chenin Blanc',
    origin: 'Loire Valley, France',
    price: '$22',
    description: 'A crisp and refreshing Chenin Blanc featuring delicate floral notes and a bright, citrusy palate. Crafted at Chateau de Chaintres.',
    image: 'https://cdn.magicpatterns.com/uploads/8DxNiTzRKHiF4u4n4eaC5L/Screenshot_2026-03-23_at_9.09.11_PM.png',
    badge: 'Signature White',
    imageScale: 'scale-[1.3]'
  }, {
    name: 'Les Hirondelles',
    variety: 'Rosé',
    origin: 'Loire Valley, France',
    price: '$19',
    description: 'Crisp, dry, and elegantly pale. Perfect for celebrations with delicate notes of strawberry and citrus. Crafted at Chateau de Chaintres.',
    image: 'https://cdn.magicpatterns.com/uploads/cTccvparp2Z6bncj1faiiB/Screenshot_2026-03-23_at_8.56.30_PM.png',
    badge: 'Rosé'
  }, {
    name: 'Blanc de Noir',
    variety: 'Sparkling',
    origin: 'Loire Valley, France',
    price: '$25',
    description: 'An elegant sparkling wine perfect for celebrations, offering fine bubbles and notes of green apple and brioche.',
    image: 'https://cdn.magicpatterns.com/uploads/gevVDRX6R6AEyKTmRbKtjb/Screenshot_2026-03-23_at_9.07.43_PM.png',
    badge: 'Sparkling'
  }];
  return <section id="collection" className="py-24 bg-cream-50" data-id="element-262">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-263">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6" data-id="element-264">
          <div className="max-w-2xl" data-id="element-265">
            <span className="text-wine font-semibold tracking-wider uppercase text-sm mb-4 block" data-id="element-266">
              Curated Selection
            </span>
            <h2 className="font-playfair text-4xl md:text-5xl text-charcoal font-bold mb-4" data-id="element-267">
              Our{' '}
              <span className="italic text-wine font-normal" data-id="element-268">Collection</span>
            </h2>
            <p className="text-lg text-brown/70 mb-4" data-id="element-269">
              Discover our hand-picked selection offered in exclusive
              partnership with the esteemed{' '}
              <a href="https://chaintres.fr/" target="_blank" rel="noopener noreferrer" className="text-wine font-medium underline hover:text-gold transition-colors" data-id="element-270">
                Chateau de Chaintres
              </a>{' '}
              vineyards in the Loire Valley.
            </p>
          </div>
          <a href="#pricing" className="inline-flex items-center text-wine font-medium hover:text-gold transition-colors whitespace-nowrap" data-id="element-271">
            View Pricing Details <span className="ml-2" data-id="element-272">→</span>
          </a>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-273">
          {wines.map((wine, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} className="group bg-white rounded-sm overflow-hidden shadow-subtle hover:shadow-elevated transition-all duration-300 flex flex-col h-full border border-cream-200" data-id="element-274">
              {/* Image Container */}
              <div className="relative h-80 overflow-hidden p-6 flex items-center justify-center bg-gray-200 cursor-pointer" onClick={() => navigate('/design#choose-wine')} data-id="element-275">
                {wine.badge && <div className="absolute top-4 left-4 bg-wine text-white text-xs font-bold uppercase tracking-wider py-1 px-3 rounded-sm z-10" data-id="element-276">
                    {wine.badge}
                  </div>}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-id="element-277">
                  <div className="w-40 h-full bg-white/80 blur-2xl rounded-full" data-id="element-278"></div>
                </div>
                <img src={wine.image} alt={wine.name} className={`h-full w-auto object-contain group-hover:scale-105 transition-transform duration-500 relative z-[1] ${(wine as any).imageScale || ''}`} data-id="element-279" />
                {/* Hover hint */}
                <div className="absolute inset-0 bg-charcoal/0 group-hover:bg-charcoal/10 transition-colors duration-300 z-[2] flex items-center justify-center" data-id="element-280">
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-white font-semibold text-sm bg-charcoal/60 px-4 py-2 rounded-sm backdrop-blur-sm" data-id="element-281">
                    Click to Label
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 flex flex-col flex-grow" data-id="element-282">
                <div className="text-xs text-gold font-bold uppercase tracking-wider mb-1" data-id="element-283">
                  {wine.origin}
                </div>
                <div className="text-xs text-wine/70 font-semibold uppercase tracking-wider mb-3" data-id="element-284">
                  {wine.variety}
                </div>
                <h3 className="font-playfair text-xl font-bold text-charcoal mb-2 line-clamp-2" data-id="element-285">
                  {wine.name}
                </h3>
                <p className="text-brown/70 text-sm mb-6 flex-grow" data-id="element-286">
                  {wine.description}
                </p>

                <div className="flex items-center gap-2 mb-4" data-id="element-287">
                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-50 border border-green-200 rounded-full" data-id="element-288">
                    <LeafIcon className="w-3.5 h-3.5 text-green-600" data-id="element-289" />
                    <span className="text-[10px] font-bold text-green-700 uppercase tracking-wider" data-id="element-290">
                      Bio Certified
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>

      {/* Label Me Modal */}
      <AnimatePresence data-id="element-291">
        {selectedWine !== null && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="fixed inset-0 z-50 flex items-center justify-center bg-charcoal/70 backdrop-blur-sm p-4" onClick={() => setSelectedWine(null)} data-id="element-292">
            <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeOut'
        }} className="relative bg-cream-50 rounded-sm shadow-2xl max-w-md w-full p-8 flex flex-col items-center" onClick={e => e.stopPropagation()} data-id="element-293">
              {/* Close button */}
              <button onClick={() => setSelectedWine(null)} className="absolute top-4 right-4 text-brown/50 hover:text-charcoal transition-colors" data-id="element-294">
                <XIcon className="w-5 h-5" data-id="element-295" />
              </button>

              {/* Bottle with white label */}
              <div className="relative h-[28rem] flex items-center justify-center mb-6" data-id="element-296">
                <img src={wines[selectedWine].image} alt={wines[selectedWine].name} className={`h-full w-auto object-contain ${(wines[selectedWine] as any).imageScale || ''}`} data-id="element-297" />
                {/* Luxurious white label on the bottle */}
                <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: 0.25,
              duration: 0.5,
              ease: 'easeOut'
            }} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none" data-id="element-298">
                  <div className="relative bg-white/95 backdrop-blur-[2px] shadow-[0_4px_30px_rgba(0,0,0,0.15)] px-8 py-6 flex flex-col items-center" style={{
                width: '140px'
              }} data-id="element-299">
                    {/* Top gold ornament */}
                    <div className="flex items-center gap-2 mb-3" data-id="element-300">
                      <div className="w-6 h-px bg-gold/60" data-id="element-301"></div>
                      <div className="w-1.5 h-1.5 border border-gold/60 rotate-45" data-id="element-302"></div>
                      <div className="w-6 h-px bg-gold/60" data-id="element-303"></div>
                    </div>

                    <span className="font-playfair text-lg font-bold text-charcoal italic leading-tight text-center" data-id="element-304">
                      Label
                    </span>
                    <span className="font-playfair text-lg font-bold text-charcoal italic leading-tight text-center" data-id="element-305">
                      Me
                    </span>

                    {/* Bottom gold ornament */}
                    <div className="flex items-center gap-2 mt-3" data-id="element-306">
                      <div className="w-6 h-px bg-gold/60" data-id="element-307"></div>
                      <div className="w-1.5 h-1.5 border border-gold/60 rotate-45" data-id="element-308"></div>
                      <div className="w-6 h-px bg-gold/60" data-id="element-309"></div>
                    </div>

                    {/* Subtle gold border inset */}
                    <div className="absolute inset-[3px] border border-gold/20 pointer-events-none" data-id="element-310"></div>
                  </div>
                </motion.div>
              </div>

              <h3 className="font-playfair text-2xl font-bold text-charcoal mb-2" data-id="element-311">
                {wines[selectedWine].name}
              </h3>
              <p className="text-brown/60 text-sm text-center mb-6" data-id="element-312">
                Make this bottle yours with a custom label
              </p>
              <a href="/design" onClick={() => setSelectedWine(null)} className="px-8 py-3 bg-wine text-white rounded-sm font-medium hover:bg-wine-700 transition-all duration-300 shadow-lg" data-id="element-313">
                Customize This Bottle
              </a>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}